import { useMemo, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import ContactPhoneIcon from '@mui/icons-material/ContactPhoneOutlined'
import { ChampDate } from '../../ui/ChampDate.js'
import {
  contactExportable,
  nomDuCarnet,
  versCarnet,
  type ContactExportable,
  type Customer,
} from '@iobiz/shared'

/**
 * Verser les fiches clients dans le CARNET D'ADRESSES du téléphone.
 *
 * ⚠️ Ce que cet écran ne peut PAS faire, et pourquoi : aucune API web n'écrit
 * dans les contacts, ni sur Android ni sur iOS. La `Contact Picker API` ne fait
 * que lire, et seulement ce que l'utilisateur désigne. Le refus est délibéré
 * des deux côtés — un site capable d'écrire dans le répertoire pourrait aussi
 * le remplir de spam.
 *
 * ⭐ Le fichier vCard, lui, passe : les deux systèmes le reconnaissent
 * nativement. Une tape sur iOS (« Ajouter tous les contacts »), l'import du
 * répertoire sur Android. L'écran le dit franchement plutôt que de promettre
 * une magie qui n'existe pas.
 *
 * ⭐ Tout se fait SUR L'APPAREIL, depuis la réplique locale : pas d'appel
 * réseau, donc ça marche au comptoir sans connexion — et le fichier de la
 * clientèle ne fait pas un aller-retour par le serveur pour revenir identique.
 */

type Portee = 'tous' | 'nouveaux' | 'selection'

/** Date du dernier versement, par boutique. */
const cle = (storeId: string): string => `iobiz.contacts.dernierExport.${storeId}`

function lireDernierExport(storeId: string): string | null {
  try {
    return localStorage.getItem(cle(storeId))
  } catch {
    // Navigation privée, stockage refusé : on perd l'incrémental, pas l'export.
    return null
  }
}

/** « AAAA-MM-JJ » depuis les composantes LOCALES — jamais `toISOString()`. */
function enJour(d: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function versContact(c: Customer): ContactExportable {
  return {
    id: c.id,
    displayName: c.displayName,
    firstName: c.firstName,
    lastName: c.lastName,
    companyName: c.companyName,
    email: c.email,
    phone: c.phone,
    mobile: c.mobile,
    addressLine1: c.addressLine1,
    addressLine2: c.addressLine2,
    postalCode: c.postalCode,
    city: c.city,
    countryCode: c.countryCode,
    code: c.code,
  }
}

const estIOS = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPadOS se présente comme un Mac depuis 2020 ; le tactile le trahit.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

const estAndroid = (): boolean => /Android/i.test(navigator.userAgent)

export function ContactsTelephoneDialog({
  open,
  storeId,
  nomBoutique,
  pays,
  clients,
  selection,
  onClose,
}: {
  open: boolean
  storeId: string
  nomBoutique: string
  pays: string | null
  /** Les fiches de la réplique locale, telles que la liste les affiche. */
  clients: Customer[]
  /** Identifiants cochés dans le tableau, s'il y en a. */
  selection: string[]
  onClose: () => void
}): JSX.Element {
  const dernier = lireDernierExport(storeId)
  const [portee, setPortee] = useState<Portee>(selection.length > 0 ? 'selection' : 'tous')
  /**
   * ⭐ La date est MODIFIABLE, et proposée même quand rien n'a encore été versé.
   *
   * Elle ne l'était pas : l'option n'apparaissait qu'après un premier envoi, si
   * bien qu'on ne pouvait pas savoir qu'elle existait. Elle part de la date du
   * dernier versement quand il y en a un, du mois écoulé sinon — et se change,
   * parce que « depuis mon dernier envoi » et « depuis la rentrée » sont deux
   * questions également légitimes.
   */
  const [depuis, setDepuis] = useState<string>(() => {
    if (dernier) return dernier.slice(0, 10)
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return enJour(d)
  })
  const [prefixer, setPrefixer] = useState(true)
  const [occupe, setOccupe] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  /**
   * ⚠️ Comparaison de chaînes ISO, volontairement : « 2026-09-07T14:22:31Z »
   * est bien supérieur à « 2026-09-07 », donc la journée choisie est INCLUSE.
   * Passer par des `Date` aurait rouvert la question du fuseau pour rien.
   */
  const nouveaux = useMemo(
    () => (depuis ? clients.filter((c) => c.updatedAt >= depuis) : clients),
    [clients, depuis],
  )

  const retenus = useMemo(() => {
    const base =
      portee === 'selection'
        ? clients.filter((c) => selection.includes(c.id))
        : portee === 'nouveaux'
          ? nouveaux
          : clients
    // ⭐ Une fiche sans numéro ni courriel ne fait pas un contact : elle
    // encombrerait le répertoire d'une entrée vide qu'on ne peut ni appeler ni
    // écrire.
    return base.map(versContact).filter(contactExportable)
  }, [portee, clients, selection, nouveaux])

  const ecartes =
    (portee === 'selection' ? selection.length : portee === 'nouveaux' ? nouveaux.length : clients.length) -
    retenus.length

  async function verser(): Promise<void> {
    setOccupe(true)
    setErreur(null)
    setMessage(null)
    try {
      const contenu = versCarnet(retenus, {
        prefixe: prefixer ? nomBoutique : null,
        pays,
      })
      const nom = nomDuCarnet(nomBoutique)
      const type = 'text/vcard;charset=utf-8'

      /**
       * ⭐ Le PARTAGE d'abord : sur iOS, c'est lui qui ouvre la feuille système
       * d'où l'on choisit « Contacts ». Un simple lien de téléchargement y
       * atterrit dans Fichiers, d'où il faut retrouver le .vcf à la main.
       */
      const fichier = new File([contenu], nom, { type })
      if (navigator.canShare?.({ files: [fichier] })) {
        await navigator.share({ files: [fichier], title: `Contacts ${nomBoutique}` })
        setMessage('Fichier transmis. Choisissez « Contacts » pour l’ajouter au répertoire.')
      } else {
        const url = URL.createObjectURL(new Blob([contenu], { type }))
        const lien = document.createElement('a')
        lien.href = url
        lien.download = nom
        document.body.appendChild(lien)
        lien.click()
        lien.remove()
        // ⚠️ Révocation DIFFÉRÉE : révoquer aussitôt annule le téléchargement
        // sur plusieurs navigateurs, qui n'ont pas encore lu le blob.
        setTimeout(() => URL.revokeObjectURL(url), 30_000)
        setMessage(`Fichier « ${nom} » enregistré. Ouvrez-le pour l’importer.`)
      }

      try {
        localStorage.setItem(cle(storeId), new Date().toISOString())
      } catch {
        // Sans stockage, l'export a quand même eu lieu : on ne perd que la
        // possibilité de proposer l'incrémental la prochaine fois.
      }
    } catch (e) {
      // ⚠️ Annuler la feuille de partage lève `AbortError`. Ce n'est pas une
      // panne : afficher une erreur rouge pour un geste volontaire serait
      // incompréhensible.
      if (e instanceof DOMException && e.name === 'AbortError') setMessage(null)
      else setErreur(e instanceof Error ? e.message : 'Export impossible.')
    } finally {
      setOccupe(false)
    }
  }

  const ios = estIOS()
  const android = estAndroid()

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <ContactPhoneIcon sx={{ color: 'primary.main' }} />
        Ajouter les clients au téléphone
      </DialogTitle>

      <DialogContent dividers>
        {erreur && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setErreur(null)}>
            {erreur}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 4 }} onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}

        {/**
         * ⚠️ On DIT que l'application n'écrit pas elle-même dans le répertoire.
         * Laisser croire le contraire ferait chercher un bogue là où il y a une
         * règle des systèmes d'exploitation.
         */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <AlertTitle>Comment ça se passe</AlertTitle>
          {ios ? (
            <>
              L’application prépare un fichier de contacts et le transmet à iOS. Choisissez{' '}
              <strong>Contacts</strong> dans la liste, puis{' '}
              <strong>« Ajouter tous les contacts »</strong>.
            </>
          ) : android ? (
            <>
              L’application prépare un fichier de contacts. Android vous proposera de l’ouvrir avec{' '}
              <strong>Contacts</strong>, qui l’importera dans le répertoire.
            </>
          ) : (
            <>
              Le fichier va être téléchargé sur cet ordinateur. Pour l’installer sur un téléphone,
              ouvrez cette page <strong>depuis le téléphone</strong> — ou envoyez-vous le fichier
              par courriel et ouvrez-le là-bas.
            </>
          )}{' '}
          Aucun navigateur ne peut écrire directement dans le répertoire : c’est le téléphone qui
          fait l’ajout, et il vous le demandera.
        </Alert>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Quels clients ?
        </Typography>

        <RadioGroup value={portee} onChange={(_, v) => setPortee(v as Portee)}>
          {selection.length > 0 && (
            <FormControlLabel
              value="selection"
              control={<Radio size="small" />}
              label={`Les ${selection.length} fiches cochées`}
            />
          )}

          {/* ⭐ « Mettre à jour » plutôt que « tout renvoyer » : c'est le geste
              du quotidien, et il réduit d'autant les doublons. */}
          <FormControlLabel
            value="nouveaux"
            control={<Radio size="small" />}
            label={
              <>
                Nouveaux et modifiés depuis une date
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {nouveaux.length} fiche{nouveaux.length > 1 ? 's' : ''}
                  {dernier
                    ? ` · dernier versement le ${new Date(dernier).toLocaleDateString('fr-FR')}`
                    : ' · aucun versement encore enregistré sur cet appareil'}
                </Typography>
              </>
            }
          />

          {portee === 'nouveaux' && (
            <Box sx={{ pl: 7, pr: 2, pb: 2, maxWidth: 280 }}>
              <ChampDate
                label="À partir du"
                value={depuis}
                onChange={setDepuis}
                max={enJour(new Date())}
              />
            </Box>
          )}

          <FormControlLabel
            value="tous"
            control={<Radio size="small" />}
            label={`Tous les clients affichés (${clients.length})`}
          />
        </RadioGroup>

        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              size="small"
              checked={prefixer}
              onChange={(e) => setPrefixer(e.target.checked)}
            />
          }
          label={
            <>
              Préfixer du nom de la boutique
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                « {nomBoutique} · Awa Boosta » — pour les retrouver dans un répertoire personnel
              </Typography>
            </>
          }
        />

        <Box sx={{ mt: 4 }}>
          <Typography variant="body2">
            <strong>{retenus.length}</strong> contact{retenus.length > 1 ? 's' : ''} à ajouter.
          </Typography>
          {ecartes > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {ecartes} fiche{ecartes > 1 ? 's' : ''} écartée{ecartes > 1 ? 's' : ''} : ni numéro ni
              courriel.
            </Typography>
          )}
        </Box>

        {/* ⚠️ Le doublon est le désagrément principal de ce format, et il ne
            dépend pas de nous : chaque fiche porte un identifiant stable, mais
            tous les répertoires ne s'en servent pas. Mieux vaut prévenir. */}
        {portee === 'tous' && dernier && (
          <Alert severity="warning" sx={{ mt: 4 }}>
            Vous avez déjà versé ce carnet. Certains téléphones recréeront les contacts au lieu de
            les mettre à jour : préférez « nouveaux et modifiés » si vous n’en attendez pas une
            remise à plat.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Fermer</Button>
        <Button
          variant="contained"
          disabled={occupe || retenus.length === 0}
          onClick={() => void verser()}
        >
          {ios || android ? 'Ajouter au téléphone' : 'Télécharger le fichier'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
